import React, { useState, useRef } from 'react';

import { getModuleName } from '../../../../shared/services/unit-type.service';
import { UserUIModel } from '../../../../../shared/ui-models/user.ui-model';
import SearchIcon from '@material-ui/icons/Search';
import { store } from 'react-notifications-component';

import { addUser } from '../../../../shared/services/data.service';
import {
  InputText,
  InputTextErrorMode
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import { useTranslation } from 'react-i18next';
import styles from './assign-salesPerson-modal.module.scss';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize
} from '../../../../../shared/components/core-ui/button/button.component';

import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import { ErrorInfo, isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { FormElementData } from '../../../../../shared/interfaces/form-element-data.interface';
import { UnitTypeUiModel } from '../../../../../shared/ui-models/unit-type.ui-model';
import { UserRole } from '../../../../../shared/enums/user-role.enum';
import PhoneInput from 'react-phone-input-2';
import { getUserByPhone } from '../../../../../shared/services/data/user.data';
import ReCAPTCHA from 'react-google-recaptcha';
import { GOOGLE_CAPTCHA_KEY } from '../../../../../shared/config/routes-config';

const captchaKEy = GOOGLE_CAPTCHA_KEY;

export interface AssignSalesPersonModalProps {
  config: {
    showModal: boolean;
  };
  eventHandlers: {
    closeHandler: () => void;
    chooseUser: (unitTypeModel: UnitTypeUiModel) => void;
  };
}

/**
 * functional component AssignSalesPersonModal
 * @param {AssignSalesPersonModalProps}
 */
export const AssignSalesPersonModal: React.FC<AssignSalesPersonModalProps> = ({
  config,
  eventHandlers
}) => {
  const { t } = useTranslation();
  const isMobileView = useWindowResizer();
  const [user, setUser] = useState<UserUIModel>();
  const [foundUsers, setFoundUsers] = useState<UserUIModel[]>([]);
  const [showUserSelection, setShowUserSelection] = useState<boolean>(false);

  const [searchNumber, setSearchNumber] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });
  const [name, setName] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });
  const [email, setEmail] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });
  const [phone, setPhone] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });

  const [showSearchResult, setShowSearchResult] = useState<boolean>(false);
  const [assignUser, setAssignUser] = useState<boolean>(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleAddUser = async (createUser: Partial<UserUIModel>) => {
    store.removeNotification('add-user-failed');
    const token = await recaptchaRef?.current?.executeAsync();
    addUser({ ...createUser, token: token || '' }).then((response: UserUIModel | ErrorInfo) => {
      if (!isErrorInfo(response)) {
        eventHandlers.chooseUser({
          partnerId: response.id,
          partnerEmail: response.email,
          partnerPhone: response.phone,
          partnerUsername: response.name
        });
        eventHandlers.closeHandler();
      } else {
        store.addNotification({
          id: 'add-user-failed',
          title: t(
            'UNIT_TYPE_MODULE.LIST_CONTAINER.ASSIGN_PARTNER_MODAL.NOTIFICATIONS.CREATION_FAILED.TITLE'
          ),
          message: t(
            'UNIT_TYPE_MODULE.LIST_CONTAINER.ASSIGN_PARTNER_MODAL.NOTIFICATIONS.CREATION_FAILED.MESSAGE'
          ),
          type: 'danger',
          insert: 'top',
          container: 'top-right',
          animationIn: ['animate__animated', 'animate__fadeIn'],
          animationOut: ['animate__animated', 'animate__fadeOut'],
          dismiss: {
            duration: 3000,
            onScreen: true
          }
        });
      }
    });
  };

  const handleSelectUser = (selectedUser: UserUIModel) => {
    setUser(selectedUser);
    setAssignUser(false);
    setShowUserSelection(false);
  };

  const handleGetUserByPhoneNumber = (phoneNumber: string) => {
    getUserByPhone(phoneNumber, getModuleName(), {
      returnAll: true
    }).then((response: UserUIModel[] | ErrorInfo) => {
      setShowSearchResult(true);
      if (!isErrorInfo(response)) {
        const users = Array.isArray(response) ? response : [response];
        setFoundUsers(users);
        setAssignUser(false);

        if (users.length === 1) {
          handleSelectUser(users[0]);
        } else if (users.length > 1) {
          setUser(undefined);
          setShowUserSelection(true);
        }
      } else {
        setUser(undefined);
        setFoundUsers([]);
        setShowUserSelection(false);
        setAssignUser(true);
        store.addNotification({
          id: 'get-user-failed',
          title: t(
            'UNIT_TYPE_MODULE.LIST_CONTAINER.ASSIGN_PARTNER_MODAL.NOTIFICATIONS.SEARCH_FAILED.TITLE'
          ),
          message: t(
            'UNIT_TYPE_MODULE.LIST_CONTAINER.ASSIGN_PARTNER_MODAL.NOTIFICATIONS.SEARCH_FAILED.MESSAGE'
          ),
          type: 'danger',
          insert: 'top',
          container: 'top-right',
          animationIn: ['animate__animated', 'animate__fadeIn'],
          animationOut: ['animate__animated', 'animate__fadeOut'],
          dismiss: {
            duration: 3000,
            onScreen: true
          }
        });
      }
    });
  };
  return (
    <Modal
      config={{
        showModal: config.showModal,
        fullScreen: false,
        showClose: true,
        className: 'partner-Modal'
      }}
      eventHandlers={{
        closeModalHandler: () => {
          eventHandlers.closeHandler();
        }
      }}
    >
      <div className={styles['assign-salesPerson-container__add-user']}>
        <div className={styles['assign-salesPerson-container__add-user__title']}>
          Select Sales Person
        </div>
        <div className={styles['assign-salesPerson-container__add-user__description']}>
          <div className={styles['assign-salesPerson-container__add-user__description--search']}>
            search by phone number
            <div
              className={
                styles['assign-salesPerson-container__add-user__description--search--input']
              }
            >
              <PhoneInput
                country={'eg'}
                inputProps={{
                  name: 'search by phone number',
                  required: true
                }}
                value={searchNumber.value}
                onChange={(phone) => {
                  phone
                    ? setSearchNumber({ value: `+${phone}`, isValid: true })
                    : setSearchNumber({ value: `${phone}`, isValid: false });
                }}
              />
              <Button
                onClick={() => {
                  handleGetUserByPhoneNumber(searchNumber.value);
                }}
                tracking={{
                  action: 'search',
                  category: 'guest',
                  label: 'add-guest'
                }}
                size={isMobileView ? ButtonSize.full : ButtonSize.medium}
              >
                <SearchIcon fontSize={'large'} color="inherit" />
              </Button>
            </div>
          </div>
        </div>{' '}
        {showUserSelection && foundUsers.length > 1 && showSearchResult && (
          <div className={styles['user-selection-container']}>
            <div className={styles['user-selection-title']}>
              Multiple accounts found for this phone number. Please select one:
            </div>
            <div className={styles['user-selection-list']}>
              {foundUsers.map((foundUser) => (
                <div key={foundUser.id} className={styles['user-selection-item']}>
                  <div className={styles['user-selection-item-content']}>
                    <div className={styles['user-selection-item-info']}>
                      <span className={styles['label']}>Name:</span>
                      <span>{foundUser.name}</span>
                    </div>
                    <div className={styles['user-selection-item-info']}>
                      <span className={styles['label']}>Email:</span>
                      <span>{foundUser.email}</span>
                    </div>
                    <div className={styles['user-selection-item-info']}>
                      <span className={styles['label']}>Phone:</span>
                      <span>{foundUser.phone}</span>
                    </div>
                  </div>
                  <Button onClick={() => handleSelectUser(foundUser)} size={ButtonSize.small}>
                    Select
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
        {user && !assignUser && !showUserSelection && showSearchResult && (
          <div>
            <div className={styles['partner-info-container-content']}>
              <div className={styles['partner-info-container-content-info']}>
                <label>id :</label>
                <p> {user?.id}</p>
              </div>
              <div className={styles['partner-info-container-content-info']}>
                <label>name :</label> <p>{user?.name}</p>
              </div>
              <div className={styles['partner-info-container-content-info']}>
                <label>email :</label> <p>{user?.email}</p>
              </div>
              <div className={styles['partner-info-container-content-info']}>
                <label>phone :</label> <p>{user?.phone}</p>
              </div>
            </div>
            <div className={styles['assign-salesPerson-container__add-user--actions']}>
              <Button
                onClick={() => {
                  eventHandlers.chooseUser({
                    partnerId: user.id,
                    partnerEmail: user.email,
                    partnerPhone: user.phone,
                    partnerUsername: user.name
                  });
                }}
                tracking={{
                  action: 'assign',
                  category: 'guest',
                  label: 'assign-guest-modal'
                }}
                size={isMobileView ? ButtonSize.full : ButtonSize.medium}
              >
                confirm
              </Button>
            </div>
          </div>
        )}
        {assignUser && showSearchResult && (
          <>
            <div className={styles['assign-salesPerson-container__add-user__assign']}>
              <div className={styles['assign-salesPerson-container__add-user__description']}>
                Name
                <InputText
                  data={{
                    defaultValue: '',
                    errorMessage: 'name is required',
                    placeholder: 'Name'
                  }}
                  config={{
                    maxRows: 1,
                    autoFocus: true,
                    required: true,
                    showErrorMode: InputTextErrorMode.instant
                  }}
                  eventHandlers={{
                    onChangeHandler: (value: string, isValid: boolean) => {
                      setName({ value, isValid });
                    }
                  }}
                />
              </div>
              <div className={styles['assign-salesPerson-container__add-user__description']}>
                Email
                <InputText
                  data={{
                    defaultValue: '',
                    errorMessage: 'Email is required',
                    placeholder: 'Email'
                  }}
                  config={{
                    maxRows: 1,
                    required: true,
                    showErrorMode: InputTextErrorMode.instant
                  }}
                  eventHandlers={{
                    onChangeHandler: (value: string, isValid: boolean) => {
                      setEmail({ value, isValid });
                    }
                  }}
                />
              </div>
              <div className={styles['assign-salesPerson-container__add-user__description']}>
                Phone Number
                <PhoneInput
                  country={'eg'}
                  inputProps={{
                    name: 'Phone number',
                    required: true
                  }}
                  value={phone.value}
                  onChange={(phone) => {
                    setPhone({ value: `+${phone}`, isValid: true });
                  }}
                />
              </div>
            </div>

            <div className={styles['assign-salesPerson-container__add-user--actions']}>
              <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={captchaKEy || ''} />
              <Button
                disabled={!name.isValid || !email.isValid || !phone.isValid}
                onClick={() => {
                  handleAddUser({
                    name: name.value,
                    email: email.value,
                    phone: phone.value,
                    role: UserRole.SALES_PERSON
                  });
                }}
                tracking={{
                  action: 'submit',
                  category: 'guest',
                  label: 'add-guest-modal'
                }}
                size={isMobileView ? ButtonSize.full : ButtonSize.medium}
              >
                Add User
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
